import type { PostHog } from "posthog-js";

export type AnalyticsEvent =
  | "search_submitted"
  | "search_results_loaded"
  | "detail_viewed"
  | "ticket_purchase_started"
  | "checkout_started"
  | "payment_submitted"
  | "purchase_completed"
  | "checkout_error";

const CAMPAIGN_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

export function sanitizeAnalyticsUrl(value: string): string {
  try {
    const url = new URL(value);
    url.username = "";
    url.password = "";
    url.hash = "";
    url.pathname = sanitizeAnalyticsPath(url.pathname);
    for (const key of [...url.searchParams.keys()]) {
      if (!CAMPAIGN_PARAMS.includes(key)) url.searchParams.delete(key);
    }
    return url.toString();
  } catch {
    return "";
  }
}

function sanitizeAnalyticsPath(value: string): string {
  return value.replace(/^((?:\/[a-z]{2})?\/invite)\/[^/]+/, "$1/[redacted]");
}

export function sanitizeAnalyticsProperties(
  properties: Record<string, unknown>,
) {
  for (const [key, value] of Object.entries(properties)) {
    if (/url|referrer/i.test(key) && typeof value === "string") {
      properties[key] = sanitizeAnalyticsUrl(value);
    } else if (/pathname$/i.test(key) && typeof value === "string") {
      properties[key] = sanitizeAnalyticsPath(value);
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      properties[key] = sanitizeAnalyticsProperties(
        value as Record<string, unknown>,
      );
    }
  }
  delete properties.$title;
  return properties;
}

export function captureAnalyticsEvent(
  client: PostHog | undefined,
  consent: boolean,
  name: AnalyticsEvent,
  properties: Record<string, unknown> = {},
): boolean {
  if (!client || !consent) return false;
  try {
    return !!client.capture(name, properties);
  } catch {
    return false;
  }
}

export function checkoutAnalyticsProperties(payload: {
  eventId?: string;
  establishmentId?: string;
  transactionId?: string;
  ticketEntries?: unknown[];
  totalPrice?: number;
  currency?: string;
  free?: boolean;
}) {
  return {
    event_id: payload.eventId,
    establishment_id: payload.establishmentId,
    transaction_id: payload.transactionId,
    ticket_count: payload.ticketEntries?.length ?? 0,
    order_value: payload.totalPrice,
    currency: payload.currency,
    is_free: payload.free === true,
  };
}

const PURCHASE_STORAGE_KEY = "upfest-analytics-purchases";
const recordedPurchases = new Set<string>();

export function captureCompletedPurchase(
  transactionId: string,
  capture: (properties: Record<string, unknown>) => boolean,
  properties: Record<string, unknown>,
  storage?: Pick<Storage, "getItem" | "setItem">,
) {
  if (!transactionId || recordedPurchases.has(transactionId)) return false;
  let previous: string[] = [];
  try {
    const saved: unknown = JSON.parse(
      storage?.getItem(PURCHASE_STORAGE_KEY) ?? "[]",
    );
    if (Array.isArray(saved))
      previous = saved.filter((id): id is string => typeof id === "string");
  } catch {
    // Storage is optional; payment and ticket delivery must keep working.
  }
  if (previous.includes(transactionId)) return false;
  if (
    !capture({
      ...properties,
      transaction_id: transactionId,
      $insert_id: `purchase_completed:${transactionId}`,
    })
  )
    return false;
  recordedPurchases.add(transactionId);
  // shortcut: retain the last 100 conversions, use server tracking for durable accounting.
  if (recordedPurchases.size > 100)
    recordedPurchases.delete(recordedPurchases.values().next().value!);
  try {
    storage?.setItem(
      PURCHASE_STORAGE_KEY,
      JSON.stringify([...previous, transactionId].slice(-100)),
    );
  } catch {
    // In-memory deduplication remains available when storage is blocked.
  }
  return true;
}
